/**
 * MagicLinkLogin — resolves a one-tap login URL from the SMS.
 *
 * URL format:  /m/<token>
 * On mount: POST /api/auth/magic/<token>  →  receives {token, user}
 *           → log the user in and redirect by status.
 *
 * On error: shows an inline error with a "Zur Anmeldung" button.
 */
import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Loader2, KeyRound, AlertTriangle } from "lucide-react";
import { Button } from "@/components/ui/button";
import { NovatusLogo } from "@/components/NovatusLogo";
import { useAuth } from "@/contexts/AuthContext";
import api from "@/lib/api";
import { postLoginDestination } from "@/lib/postLoginRoute";

// Module-level dedup-cache. React 18 StrictMode fully unmounts + remounts
// components in dev → useRef does NOT survive the cycle. By keeping the
// in-flight promise here we guarantee the token is consumed exactly once
// regardless of how many times the component mounts.
const inflightMagicCache = new Map();

export default function MagicLinkLogin() {
  const { token } = useParams();
  const navigate = useNavigate();
  const { login } = useAuth();
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!token) {
      setError("Kein Token im Anmeldelink.");
      return undefined;
    }
    let cancelled = false;
    let promise = inflightMagicCache.get(token);
    if (!promise) {
      promise = api
        .get(`/auth/magic/${encodeURIComponent(token)}`)
        .then((r) => ({ ok: true, data: r.data }))
        .catch((e) => ({
          ok: false,
          error: e?.response?.data?.detail || e?.message || "Anmeldung fehlgeschlagen.",
        }));
      inflightMagicCache.set(token, promise);
    }
    promise.then((res) => {
      if (cancelled) return;
      if (res.ok && res.data?.token && res.data?.user) {
        login(res.data.token, res.data.user);
        navigate(postLoginDestination(res.data.user), { replace: true });
      } else {
        // If we already have a valid session on THIS device (token in
        // localStorage), the user is logged in elsewhere too — silently
        // redirect to the dashboard instead of showing a dead-end error.
        try {
          const t = localStorage.getItem("token");
          if (t) {
            navigate("/dashboard", { replace: true });
            return;
          }
        } catch (_) { /* ignore */ }
        setError(res.error || "Anmeldelink ungültig.");
      }
    });
    return () => { cancelled = true; };
  }, [token, login, navigate]);

  return (
    <div className="min-h-screen bg-[#09090B] flex flex-col items-center justify-center px-6 py-10">
      <div className="w-full max-w-md text-center">
        <div className="mb-6 flex justify-center"><NovatusLogo size="md" /></div>
        {error ? (
          <div
            data-testid="magic-link-error"
            className="bg-[#18181B] border border-red-500/30 rounded-2xl p-7"
          >
            <div className="w-14 h-14 rounded-full bg-red-500/15 flex items-center justify-center mx-auto mb-4">
              <AlertTriangle className="text-red-400" size={26} />
            </div>
            <h1 className="font-display text-xl font-semibold mb-2">Anmeldelink ungültig</h1>
            <p className="text-sm text-zinc-400 leading-relaxed mb-5">{error}</p>
            <Button
              data-testid="magic-link-back-to-login"
              onClick={() => navigate("/login", { replace: true })}
              className="w-full h-12 bg-[#00E575] text-[#09090B] font-semibold hover:bg-[#00C964]"
            >
              <KeyRound size={16} className="mr-2" /> Neu anmelden
            </Button>
          </div>
        ) : (
          <div
            data-testid="magic-link-loading"
            className="bg-[#18181B] border border-zinc-800 rounded-2xl p-8"
          >
            <Loader2 className="animate-spin text-[#00E575] mx-auto mb-4" size={32} />
            <h1 className="font-display text-xl font-semibold mb-1">Sie werden angemeldet…</h1>
            <p className="text-sm text-zinc-300 font-medium">Einen Moment, bitte.</p>
          </div>
        )}
      </div>
    </div>
  );
}
